import { useState, useEffect, useMemo, type FC } from "react";
import { enqueueSnackbar } from "notistack";

import { trackEvent, EventType } from "@/shared/utils/core";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import { useLawyerEstateAccount } from "@/shared/hooks/useLawyerEstateAccount";
import { formatRelativeTime } from "@/shared/utils/formatters";
import { Button } from "@/shared/components/ui/button";
import Loader from "@/shared/components/Loader";
import { AccountSetupChecklist } from "@/pages/postloss/estate-inventory/components/AccountSetupChecklist";
import { ConnectedAccountCard } from "@/pages/postloss/estate-inventory/components/ConnectedAccountCard";
import { FinancialSummary } from "@/pages/postloss/estate-inventory/components/FinancialSummary";
import { ActivityTable, type Transaction } from "@/pages/postloss/estate-inventory/components/ActivityTable";
import { TransactionDetailPanel } from "@/pages/postloss/estate-inventory/components/TransactionDetailPanel";

import type { PlaidTransaction } from "@/shared/hooks/useTransactions";

interface LawyerEstateAccountTabProps {
  estateId: number;
  decedentFirstName: string | undefined;
  decedentFullName: string | undefined;
  hasLetters: boolean;
  hasEin: boolean;
}

interface RemindClientButtonProps {
  onRemind: () => void;
  isPending: boolean;
  isLoading: boolean;
  reminderSentAt: string | null;
}

const RemindClientButton: FC<RemindClientButtonProps> = ({
  onRemind,
  isPending,
  isLoading,
  reminderSentAt,
}) => {
  if (reminderSentAt) {
    return (
      <Button variant="outline" size="sm" disabled>
        Reminded {formatRelativeTime(reminderSentAt)}
      </Button>
    );
  }
  return (
    <Button variant="outline" size="sm" onClick={onRemind} disabled={isPending || isLoading}>
      {isPending ? "Sending..." : "Remind client"}
    </Button>
  );
};

interface LawyerReconnectBannerProps {
  onRemind: () => void;
  isPending: boolean;
  isLoading: boolean;
  reminderSentAt: string | null;
}

const LawyerReconnectBanner: FC<LawyerReconnectBannerProps> = ({
  onRemind,
  isPending,
  isLoading,
  reminderSentAt,
}) => {
  return (
    <div className="flex w-full flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-primary">
        Client's bank connection has expired — they need to sign in again.
      </p>
      <RemindClientButton onRemind={onRemind} isPending={isPending} isLoading={isLoading} reminderSentAt={reminderSentAt} />
    </div>
  );
};

export const LawyerEstateAccountTab: FC<LawyerEstateAccountTabProps> = ({
  estateId,
  decedentFirstName,
  decedentFullName,
  hasLetters,
  hasEin,
}) => {
  const { user } = useAppContext();
  const { isDemoMode } = useDemoMode();
  const lawyerUserId = user?.user_id;

  const {
    connections,
    activeConnection,
    isLoadingConnections,
    connectionsError,
    transactions,
    isLoadingTransactions,
    transactionsError,
    updateTransaction,
    isUpdating,
    reminderSentAt,
    isLoadingReminderStatus,
    sendReminder,
    isSendingReminder,
    exportPdf,
    isExporting,
  } = useLawyerEstateAccount(estateId, lawyerUserId);

  const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);

  useEffect(() => {
    trackEvent(EventType.LAWYER_ESTATE_ACCOUNT_VIEWED, { estate_id: estateId, source: "lawyer_estate_account_tab" });
  }, [estateId]);

  const handleRemind = async () => {
    if (isDemoMode) {
      enqueueSnackbar("Reminder sent.", { variant: "success" });
      return;
    }
    if (!lawyerUserId) {
      enqueueSnackbar("Not authenticated.", { variant: "error" });
      return;
    }
    try {
      const result = await sendReminder({
        estateId,
        lawyerUserId,
      });
      if (result.alreadyPending) {
        enqueueSnackbar("A reminder was already sent recently.", { variant: "info" });
      } else {
        enqueueSnackbar("Reminder sent.", { variant: "success" });
      }
    } catch {
      enqueueSnackbar("Failed to send reminder. Please try again.", { variant: "error" });
    }
  };

  const { mappedTransactions, moneyIn, moneyOut } = useMemo(() => {
    let inTotal = 0;
    let outTotal = 0;
    const mapped: Transaction[] = transactions.map((t) => {
      if (t.is_inflow) inTotal += t.amount;
      else outTotal += t.amount;
      return {
        id: t.id,
        date: t.date,
        description: t.description,
        originalDescription: t.original_description,
        category: t.category ?? undefined,
        hasReceipt: t.has_receipt,
        amount: t.amount,
        isInflow: t.is_inflow,
      };
    });
    return { mappedTransactions: mapped, moneyIn: inTotal, moneyOut: outTotal };
  }, [transactions]);

  const selectedTransaction: PlaidTransaction | null = selectedTransactionId
    ? (transactions.find((t) => t.id === selectedTransactionId) ?? null)
    : null;

  const steps = [
    {
      label: "Letters of authority",
      description: "Proves the executor can act for the estate",
      status: hasLetters ? ("completed" as const) : ("active" as const),
    },
    {
      label: "Estate EIN",
      description: "The estate's tax ID from the IRS",
      status: hasEin
        ? ("completed" as const)
        : hasLetters
          ? ("active" as const)
          : ("upcoming" as const),
    },
    {
      label: "Estate bank account opened",
      description: "A dedicated account for estate funds",
      status: connections.length > 0
        ? ("completed" as const)
        : hasEin
          ? ("active" as const)
          : ("upcoming" as const),
    },
    {
      label: "Account connected to Elayne",
      description: "Linked so you can view balance and transactions",
      status: connections.length > 0 ? ("completed" as const) : ("upcoming" as const),
    },
  ];

  if (isLoadingConnections) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="md" />
      </div>
    );
  }

  if (connectionsError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <p className="text-sm text-secondary">Could not load account info.</p>
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    );
  }

  const decedentDisplayName = decedentFirstName || "the decedent";
  const estateDisplayName = decedentFullName ? `Estate of ${decedentFullName}` : "Estate";

  if (!activeConnection) {
    return (
      <div className="flex flex-col items-center gap-8 px-4 py-8 sm:p-10">
        <div className="flex flex-col items-center gap-1">
          <h3 className="text-xl font-semibold text-center">Estate account not connected</h3>
          <p className="w-full text-center text-sm text-secondary sm:w-[440px]">
            {`Client needs to open an estate bank account for ${decedentDisplayName}'s estate and connect it so you can view the balance and transactions here.`}
          </p>
        </div>
        <AccountSetupChecklist steps={steps} />
        <RemindClientButton
          onRemind={handleRemind}
          isPending={isSendingReminder}
          isLoading={isLoadingReminderStatus}
          reminderSentAt={reminderSentAt}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-8 sm:gap-10 sm:pt-10">
      {activeConnection.status === "error" && (
        <LawyerReconnectBanner
          onRemind={handleRemind}
          isPending={isSendingReminder}
          isLoading={isLoadingReminderStatus}
          reminderSentAt={reminderSentAt}
        />
      )}

      <ConnectedAccountCard
        connection={activeConnection}
        estateDisplayName={estateDisplayName}
        onRefresh={() => undefined}
        onReconnect={() => undefined}
        isRefreshing={false}
        onExport={async () => {
          try {
            await exportPdf(decedentFullName ?? "");
          } catch {
            enqueueSnackbar("Could not generate PDF.", { variant: "error" });
          }
        }}
        isExporting={isExporting}
      />

      {isLoadingTransactions ? (
        <div className="flex items-center justify-center py-12">
          <Loader size="md" />
        </div>
      ) : transactionsError ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12">
          <p className="text-sm text-secondary">Could not load transactions.</p>
        </div>
      ) : (
        <>
          <FinancialSummary
            balance={moneyIn - moneyOut}
            moneyIn={moneyIn}
            moneyOut={moneyOut}
          />
          <ActivityTable
            transactions={mappedTransactions}
            onRowClick={(t) => setSelectedTransactionId(t.id)}
          />
        </>
      )}

      <TransactionDetailPanel
        transaction={selectedTransaction}
        onClose={() => setSelectedTransactionId(null)}
        onUpdate={(id, updates) => updateTransaction({ transactionId: id, updates })}
        isUpdating={isUpdating}
        estateId={estateId}
        disableCategory
      />
    </div>
  );
};
